import { state, type LibraryEntry } from "./state";

export const STORAGE_KEYS = {
  personLibrary: "acc_person_library_v1",
  garmentLibrary: "acc_garment_library_v1",
} as const;

export const MAX_LIBRARY_ITEMS = 10;

export type LibraryKind = "person" | "garment";

function safeParseStorage(key: string): LibraryEntry[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as LibraryEntry[]) : [];
  } catch {
    return [];
  }
}

export function hydrateLibraries(): void {
  state.personLibrary = safeParseStorage(STORAGE_KEYS.personLibrary);
  state.garmentLibrary = safeParseStorage(STORAGE_KEYS.garmentLibrary);
}

function persistLibrary(kind: LibraryKind): void {
  const storageKey =
    kind === "person" ? STORAGE_KEYS.personLibrary : STORAGE_KEYS.garmentLibrary;
  const items = kind === "person" ? state.personLibrary : state.garmentLibrary;
  try {
    localStorage.setItem(storageKey, JSON.stringify(items.slice(0, MAX_LIBRARY_ITEMS)));
  } catch {
    // localStorage may be unavailable (private mode, quota exceeded). Treat as best-effort.
  }
}

interface NormalizableLibraryItem {
  id?: string;
  name?: string;
  dataUrl: string;
  source?: string;
}

function normalizeLibraryItem(
  item: NormalizableLibraryItem,
  kind: LibraryKind,
): LibraryEntry {
  return {
    id: item.id || `${kind}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: item.name || `${kind === "person" ? "Person" : "Garment"} image`,
    dataUrl: item.dataUrl,
    source: item.source || "upload",
  };
}

export function addToLibrary(
  kind: LibraryKind,
  items: Array<Partial<LibraryEntry> & { dataUrl?: string }>,
): void {
  const normalized = items
    .filter((item): item is NormalizableLibraryItem => Boolean(item?.dataUrl))
    .map(item => normalizeLibraryItem(item, kind));
  const current = kind === "person" ? state.personLibrary : state.garmentLibrary;
  const merged = [...normalized, ...current]
    .filter((item, index, array) => {
      return array.findIndex(candidate => candidate.dataUrl === item.dataUrl) === index;
    })
    .slice(0, MAX_LIBRARY_ITEMS);

  if (kind === "person") {
    state.personLibrary = merged;
  } else {
    state.garmentLibrary = merged;
  }

  pruneOrphanedSelections(kind);
  persistLibrary(kind);
}

function pruneOrphanedSelections(kind: LibraryKind): void {
  if (kind === "person") {
    const pool = [...state.personLibrary, ...PERSON_SAMPLE_LIBRARY];
    if (
      state.selectedOutfitPerson &&
      !pool.some(p => p.dataUrl === state.selectedOutfitPerson?.dataUrl)
    ) {
      state.selectedOutfitPerson = null;
    }
    if (
      state.selectedHairPerson &&
      !pool.some(p => p.dataUrl === state.selectedHairPerson?.dataUrl)
    ) {
      state.selectedHairPerson = null;
    }
    return;
  }

  const pool = [...state.garmentLibrary, ...GARMENT_SAMPLE_LIBRARY];
  state.selectedOutfitGarments = state.selectedOutfitGarments.filter(g =>
    pool.some(item => item.dataUrl === g.dataUrl),
  );
}

export function addToPersonLibrary(items: Array<Partial<LibraryEntry> & { dataUrl?: string }>): void {
  addToLibrary("person", items);
}

export function addToGarmentLibrary(items: Array<Partial<LibraryEntry> & { dataUrl?: string }>): void {
  addToLibrary("garment", items);
}

export async function fileToDataUrl(file: File): Promise<string> {
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Failed to read file."));
    reader.readAsDataURL(file);
  });
}

// Sample mock images embedded as data URLs so the libraries are never empty.

function svgDataUrl(markup: string): string {
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(markup)}`;
}

interface PersonMockOptions {
  accent: string;
  background: string;
  top: string;
  label: string;
}

interface GarmentMockOptions {
  accent: string;
  background: string;
  label: string;
}

function buildPersonMock({ accent, background, top, label }: PersonMockOptions): string {
  return svgDataUrl(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 540 720">
      <defs>
        <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="${background}"/>
          <stop offset="100%" stop-color="#10131b"/>
        </linearGradient>
      </defs>
      <rect width="540" height="720" fill="url(#bg)"/>
      <circle cx="270" cy="180" r="74" fill="#efd9cf"/>
      <path d="M180 282c28-44 76-66 90-66 18 0 68 23 90 66v220H180z" fill="${top}"/>
      <rect x="140" y="490" width="260" height="170" rx="38" fill="${accent}" opacity="0.82"/>
      <rect x="70" y="66" width="150" height="38" rx="19" fill="rgba(255,255,255,0.12)"/>
      <text x="92" y="91" fill="#fff" font-family="Arial, sans-serif" font-size="22" font-weight="700">${label}</text>
    </svg>
  `);
}

function buildGarmentMock({ accent, background, label }: GarmentMockOptions): string {
  return svgDataUrl(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 540 620">
      <defs>
        <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="${background}"/>
          <stop offset="100%" stop-color="#0d1018"/>
        </linearGradient>
      </defs>
      <rect width="540" height="620" fill="url(#bg)"/>
      <path d="M170 132c18-26 54-44 100-44s82 18 100 44l52 82-58 44-34-36v238H210V222l-34 36-58-44z" fill="${accent}"/>
      <rect x="90" y="54" width="164" height="38" rx="19" fill="rgba(255,255,255,0.12)"/>
      <text x="112" y="80" fill="#fff" font-family="Arial, sans-serif" font-size="22" font-weight="700">${label}</text>
    </svg>
  `);
}

export const PERSON_SAMPLE_LIBRARY: LibraryEntry[] = [
  {
    id: "sample-person-editorial",
    name: "Editorial Mock",
    source: "sample",
    dataUrl: buildPersonMock({
      accent: "#6f59f6",
      background: "#25283a",
      top: "#e4dfe8",
      label: "Mock 01",
    }),
  },
  {
    id: "sample-person-neutral",
    name: "Neutral Mock",
    source: "sample",
    dataUrl: buildPersonMock({
      accent: "#e2b88a",
      background: "#252018",
      top: "#cfd5df",
      label: "Mock 02",
    }),
  },
];

export const GARMENT_SAMPLE_LIBRARY: LibraryEntry[] = [
  {
    id: "sample-garment-blazer",
    name: "Blazer Mock",
    source: "sample",
    dataUrl: buildGarmentMock({
      accent: "#e5dfd5",
      background: "#28242c",
      label: "Mock Blazer",
    }),
  },
  {
    id: "sample-garment-streetwear",
    name: "Streetwear Mock",
    source: "sample",
    dataUrl: buildGarmentMock({
      accent: "#7a5bf7",
      background: "#1b1f2d",
      label: "Mock Street",
    }),
  },
];
